<template>
  <section class="section">
    <div class="container">
      <Title>
        Packages
        <Plus
          v-if="source && !source.external && source.version >= 2"
          @click="showGenerate = true"
        ></Plus>

        <a
          v-if="source"
          @click="isRepoInformationVisible = !isRepoInformationVisible"
        >
          <span
            class="icon is-medium middle is-size-5 tooltip"
            data-tooltip="Repository reference"
          >
            <i class="mdi mdi-information-outline"></i>
          </span>
        </a>
      </Title>

      <modal
        v-model="isRepoInformationVisible"
        @hide="isRepoInformationVisible = false"
        title="Repository reference"
      >
        <div slot="body" class="content">
          <p>
            The repository API is compatible with most programs, eg. Nuget,
            Chocolatey, Nuget Package Explorer etc.
          </p>

          <h4 class="h4 text-size-5">API Endpoints</h4>
          <ul>
            <li>Download: <code>GET /package/{package id}/{version}</code></li>
            <li>
              Upload:
              <code>
                PUT /api/v2/package (key: package)
              </code>
            </li>
            <li>Delete: <code>DELETE /package/{package id}/{version}</code></li>
          </ul>

          <div class="mt-4"></div>
          <h4 class="h4 text-size-5">URL</h4>

          <p class="notification is-warning is-light">
            The URL contains an important <b>key</b> (with full permissions)
            <b>that should be kept secret</b>.
          </p>

          <div class="notification is-success is-light">
            <a
              v-if="source"
              :href="source.url"
              target="_blank"
              style="overflow-wrap: break-word"
            >
              {{ source.url }}
            </a>
          </div>
        </div>
      </modal>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="true"
        @submit="search($event)"
      ></SearchBar>

      <LoaderOne v-show="!firstLoadDone"></LoaderOne>

      <p v-show="!searching && firstLoadDone && items.length === 0">
        <i>No packages.</i>
      </p>

      <Tips
        v-if="
          source &&
            !source.external &&
            source.version >= 2 &&
            firstLoadDone &&
            items.length !== 0
        "
        path="repos"
        xKey="dismissedTips"
        property="updatePackage"
        >Update-button: Generate a new package based on another package,
        including installation arguments.
      </Tips>

      <table
        class="table is-fullwidth is-hoverable is-narrow"
        v-if="firstLoadDone && items.length !== 0"
      >
        <thead>
          <tr class="is-unselectable">
            <th>Icon</th>
            <th>Id</th>
            <th>Title</th>
            <th>Version</th>
            <th>Size</th>
            <th>Downloads</th>
            <th>Published</th>
            <th>Updated</th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <TableEffect>
          <tr
            v-for="item of items"
            :key="item.chocoPackage.Id"
            :id="item.chocoPackage.Id"
          >
            <td>
              <figure class="image is-24x24">
                <img :src="icon(item)" alt />
              </figure>
            </td>
            <td>{{ item.chocoPackage.Id }}</td>
            <td>{{ title(item) }}</td>
            <td>
              <Versions :item="item" @change="replace(item, $event)"></Versions>
            </td>

            <td>{{ round(item.chocoPackage.PackageSize / 1000000) }} MB</td>
            <td>{{ item.chocoPackage.DownloadCount }}</td>
            <td>{{ fromNow(item.chocoPackage.Published) }}</td>
            <td>{{ fromNow(item.chocoPackage.LastUpdated) }}</td>
            <td>
              <a
                :disabled="source.permission && source.permission < 1"
                class="button is-small smaller"
                target="_blank"
                :href="downloadUrl(item)"
              >
                Download
              </a>
            </td>
            <td>
              <a
                :disabled="item.source.external && item.source.version !== 2"
                class="button is-primary is-small smaller"
                @click="showModal(item, 'showMove')"
                >Move</a
              >
            </td>

            <td>
              <a
                :data-tooltip="
                  item.source.external && item.source.version !== 2
                    ? null
                    : 'Generate new package, based on this package.'
                "
                :disabled="item.source.external && item.source.version !== 2"
                class="button is-info is-small smaller"
                @click="showModal(item, 'showGenerate')"
                >Update</a
              >
            </td>

            <td>
              <a
                :disabled="item.source.external && item.source.version !== 2"
                class="button is-danger is-small smaller"
                @click="showModal(item, 'showDelete')"
                >Delete</a
              >
            </td>
          </tr>
        </TableEffect>
      </table>
    </div>
    <Move
      v-if="showMove && selectedItem"
      v-model="showMove"
      :item="selectedItem"
      :sources="$store.state.source.sources"
      @change="remove($event)"
    ></Move>

    <Delete
      v-if="showDelete && selectedItem"
      v-model="showDelete"
      :item="selectedItem"
      :sources="$store.state.source.sources"
      @change="remove($event)"
    ></Delete>

    <Generate
      v-if="showGenerate"
      v-model="showGenerate"
      :source="source"
      :item="selectedItem"
      @hide="selectedItem = null"
      @change="getPackages()"
    ></Generate>
  </section>
</template>

<style lang="css" scoped>
th {
  font-weight: normal;
}
</style>

<script>
import { fromNow, fromJson } from "@/utils/date";
import { postApi } from "@/utils/http";
import { parseAndRound } from "@/utils/string";
import { focus, getId } from "@/utils/document";
import odata from "@/utils/packageOdata";

export default {
  components: {
    Tips: () => import("@/components/main/Tips.vue"),
    TableEffect: () => import("@/components/singles/TableEffect.vue"),
    IconText: () => import("@/components/singles/IconText.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    Versions: () => import("@/components/main/repo/Versions.vue"),
    Move: () => import("@/components/main/repo/Move.vue"),
    Delete: () => import("@/components/main/repo/DeletePackage.vue"),
    LoaderOne: () => import("@/components/singles/LoaderOne.vue"),
    Plus: () => import("@/components/main/Plus.vue"),
    Generate: () => import("@/components/main/repo/Generate.vue"),
    Modal: () => import("@/components/main/Modal.vue"),
  },

  computed: {
    icon() {
      return (item) => {
        return item.chocoPackage.IconUrl && item.chocoPackage.IconUrl !== ""
          ? item.chocoPackage.IconUrl
          : "/box.png";
      };
    },

    title() {
      return (item) => {
        return item.chocoPackage.Title.length > 30
          ? `${item.chocoPackage.Title.substring(0, 30)}...`
          : item.chocoPackage.Title;
      };
    },

    fromNow() {
      return (date) => {
        return fromNow(date);
      };
    },
    round() {
      return (number) => {
        return parseAndRound(number, 2);
      };
    },
    downloadUrl() {
      return (item) =>
        `${item.source.url}package/${item.chocoPackage.Id}/${item.chocoPackage.Version}`;
    },
  },
  data() {
    return {
      firstLoadDone: false,
      searching: false,
      showInfo: false,
      showMove: false,
      showDelete: false,
      showGenerate: false,
      selectedItem: null,
      tempFilter: "",
      filter: "",
      searchId: getId(),
      items: [],
      source: null,
      isRepoInformationVisible: false,
    };
  },
  async created() {
    try {
      await this.$store.dispatch("source/fetchAll");
      this.source = this.$store.getters["source/single"](
        this.$route.params.repoUID
      );

      await this.getPackages();
      this.firstLoadDone = true;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get data.", "danger", 5000);
    }
  },
  beforeDestroy() {
    this.animationend();
  },
  methods: {
    jump(item) {
      this.$nextTick(() => {
        const el = document.getElementById(item.chocoPackage.Id);
        el.classList.add("pulse-fast-once");

        document.addEventListener("animationend", this.animationend);
      });
    },
    animationend() {
      const el = document.querySelector(".pulse-fast-once");
      if (el && el.classList) el.classList.remove("pulse-fast-once");

      document.removeEventListener("animationend", this.animationend);
    },
    showModal(item, modal) {
      if (this.source && !this.source.external && this.source.version >= 2) {
        this.selectedItem = item;
        this.$set(this, modal, true);
      }
    },
    focus: focus,
    async getPackages() {
      this.searching = true;
      const chocoResult = await postApi("choco/packages", {
        query: odata.getLatestPackagesString(15, true),
        source: this.source,
      });

      this.items = chocoResult.packages;

      this.searching = false;
      focus(this.searchId);
    },

    async search(searchStr) {
      this.searching = true;

      if (searchStr == "") {
        await this.getPackages();
        return;
      }

      searchStr = searchStr.trim().toLowerCase();
      const chocoResult = await postApi("choco/packages", {
        query: odata.getSearchString(searchStr, true, 0, 15),
        source: this.source,
      });

      this.items = chocoResult.packages;

      this.searching = false;
      focus(this.searchId);
    },
    remove(item) {
      const index = this.items.findIndex(
        (x) => x.chocoPackage.PackageHash === item.chocoPackage.PackageHash
      );

      this.$delete(this.items, index);
    },
    replace(item, newItem) {
      const index = this.items.findIndex(
        (x) => x.chocoPackage.PackageHash === item.chocoPackage.PackageHash
      );

      this.$set(this.items, index, newItem);
      this.jump(newItem);
    },
  },
};
</script>
